/**
 * Carrying a chosen API plan (Premium, Ultra, Mega) through Google sign-in. A signed-out visitor who clicks a plan button is sent to
 * sign in with returnTo = the page they were on plus ?autoBuy=api:<plan>. The plan name is the only thing carried: no email, name or
 * id. Back on the page, useAutoResumeCheckout reads the parameter, strips it and goes straight to Stripe Checkout for that plan.
 */
export const API_PLAN_IDS = ["premium", "ultra", "mega"] as const;
export type ApiPlanId = (typeof API_PLAN_IDS)[number];

export const API_AUTOBUY_PREFIX = "api:";

export function isApiPlanId(v: unknown): v is ApiPlanId {
  return typeof v === "string" && (API_PLAN_IDS as readonly string[]).includes(v);
}

/** The relative returnTo for a plan click made on `pathname` (with its own query kept, except any earlier autoBuy). */
export function returnToForApiPlan(plan: ApiPlanId, pathname: string, search = ""): string {
  const base = pathname.startsWith("/") && !pathname.startsWith("//") ? pathname : "/";
  const params = new URLSearchParams(search);
  params.set("autoBuy", `${API_AUTOBUY_PREFIX}${plan}`);
  return `${base}?${params.toString()}`;
}

/** The plan named by an autoBuy value such as "api:ultra", or null for anything else. */
export function apiPlanFromAutoBuy(autoBuy: string | null | undefined): ApiPlanId | null {
  if (!autoBuy || !autoBuy.startsWith(API_AUTOBUY_PREFIX)) return null;
  const plan = autoBuy.slice(API_AUTOBUY_PREFIX.length);
  return isApiPlanId(plan) ? plan : null;
}
